import { useEffect } from 'react';

/**
 * Inject document-head meta tags for the current page.
 *
 *   useSeoMeta({ title, description, image, type, url, keywords })
 *
 * Updates `document.title`, the description meta, and the OG / Twitter
 * card tags. Restores any pre-existing values when the page unmounts so
 * the next route doesn't inherit a stale card.
 *
 * Re-exported from the storefront barrel (`pages/StorefrontShop.jsx`)
 * so PublicSite + other consumers keep their existing import path.
 */
export default function useSeoMeta({ title, description, image, type = 'website', url, keywords } = {}) {
  useEffect(() => {
    if (!title && !description && !image) return undefined;

    const created = [];
    const upsertMeta = (key, value, attr = 'name') => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      let prev = null;
      if (el) {
        prev = el.getAttribute('content');
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', String(value));
      created.push({ el, prev });
    };

    const prevTitle = document.title;
    if (title) document.title = title;

    upsertMeta('description', description);
    upsertMeta('keywords', keywords);
    upsertMeta('og:title',       title || prevTitle, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:type',        type, 'property');
    upsertMeta('og:url',         url || (typeof window !== 'undefined' ? window.location.href : ''), 'property');
    upsertMeta('og:image',       image, 'property');
    upsertMeta('twitter:card',        image ? 'summary_large_image' : 'summary');
    upsertMeta('twitter:title',       title || prevTitle);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image',       image);

    return () => {
      created.forEach(({ el, prev }) => {
        if (prev == null) el.parentNode && el.parentNode.removeChild(el);
        else el.setAttribute('content', prev);
      });
      document.title = prevTitle;
    };
  }, [title, description, image, type, url, keywords]);
}
